<template>
  <el-autocomplete
    ref="autocompleteRef"
    v-model="query"
    class="header-search"
    :fetch-suggestions="fetchSuggestions"
    :trigger-on-focus="false"
    :debounce="250"
    :fit-input-width="true"
    highlight-first-item
    popper-class="header-search__popper"
    value-key="product_name"
    placeholder="Поиск по товарам"
    clearable
    @select="goToProduct"
  >
    <template #prefix>
      <el-icon><Search /></el-icon>
    </template>

    <template #default="{ item }">
      <div class="hs-item">
        <img
          v-if="item.image_id && item.color_alias"
          class="hs-item__img"
          :src="thumbUrl(item)"
          :alt="item.product_name"
          loading="lazy"
        />
        <div v-else class="hs-item__img hs-item__img--empty" />
        <div class="hs-item__text">
          <div class="hs-item__name">{{ item.product_name }}</div>
          <div class="hs-item__meta">
            {{ item.category_name }}
            <span v-if="item.vendor_code"> · {{ item.vendor_code }}</span>
          </div>
        </div>
      </div>
    </template>
  </el-autocomplete>
</template>

<script setup>
import { ref, nextTick } from "vue";
import { Search } from "@element-plus/icons-vue";

const MIN_QUERY_LENGTH = 2;

const router = useRouter();

const autocompleteRef = ref(null);
const query = ref("");

// Отсекаем ответы устаревших запросов: debounce не гарантирует порядок ответов
let requestId = 0;

const thumbUrl = (item) =>
  `/api/image/${item.category_alias}/${item.product_alias}/color/${item.color_alias}/${item.image_id}?size=200`;

const fetchSuggestions = async (queryString, cb) => {
  const term = (queryString || "").trim();

  if (term.length < MIN_QUERY_LENGTH) return cb([]);

  const currentRequest = ++requestId;

  try {
    const rows = await $fetch("/api/search_products", {
      query: { q: term, limit: 8 },
    });

    if (currentRequest !== requestId) return; // пришёл ответ на старый запрос

    cb(Array.isArray(rows) ? rows : []);
  } catch (error) {
    if (currentRequest !== requestId) return;
    console.error("Ошибка поиска:", error);
    cb([]);
  }
};

const goToProduct = (item) => {
  if (!item?.product_alias || !item?.category_alias) return;

  const colorQuery = item.color_alias ? `?color=${item.color_alias}` : "";
  router.push(`/catalog/${item.category_alias}/${item.product_alias}${colorQuery}`);

  query.value = "";
  nextTick(() => autocompleteRef.value?.blur?.());
};
</script>

<style scoped>
.header-search {
  width: 100%;
}
</style>

<style>
/* Popper телепортируется в body — стили не могут быть scoped.
   fit-input-width на el-autocomplete держит ширину попапа равной полю
   ввода; min/max-width ниже — лишь защита от совсем узкого/широкого поля */
.header-search__popper {
  box-sizing: border-box;
  min-width: min(400px, calc(100vw - 40px));
  max-width: calc(100vw - 40px);
}

/* fit-input-width задаёт inline width на этот блок по фактической (узкой)
   ширине поля — из-за min-width выше попап может быть шире поля, поэтому
   растягиваем список принудительно на всю ширину попапа */
.header-search__popper .el-autocomplete-suggestion {
  width: 100% !important;
}

.header-search__popper .el-autocomplete-suggestion__list li {
  height: auto;
  line-height: normal;
  padding: 6px 12px;
}

.hs-item {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
}

.hs-item__img {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  object-fit: cover;
  border-radius: 4px;
  background-color: var(--el-fill-color-light);
}

.hs-item__img--empty {
  border: 1px dashed var(--el-border-color);
}

.hs-item__text {
  overflow: hidden;
}

.hs-item__name {
  font-size: 14px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hs-item__meta {
  font-size: 12px;
  line-height: 1.3;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
